import { AlertModal } from "../../../components"
import PageWrapper from "../../../components/PageWrapper/PageWrapper"
import arrowLeft from "../../../assets/icons/Arrows/arrow-narrow-left.png"
import documentsIcon from "../../../assets/icons/Files/folder.png"
import filesFile06 from "../../../assets/icons/Files/file-06.png"
import mailIcon from "../../../assets/icons/Communication/mail-01.png"
import translateIcon from "../../../assets/translate-01.svg"
import { useAuth } from "../../../contexts/Auth/AuthContext"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { useTranslation } from "react-i18next"

const Settings = () => {
  const { t } = useTranslation()
  const { user, logout, deleteAccount } = useAuth()
  const navigate = useNavigate()

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMsg, setAlertMsg] = useState("")
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const userInitials = user?.displayName
    ? user.displayName
        .split(" ")
        .map((name) => name.charAt(0).toUpperCase())
        .join("")
    : "U"

  const handleLogout = () => setConfirmOpen(true)
  const confirmLogout = async () => {
    try {
      await logout()
      navigate("/auth/signin")
    } catch (err) {
      console.error("Logout error:", err)
      setAlertMsg(
        t("dashboard.failedToLogout", { defaultValue: "Failed to log out" })
      )
      setAlertOpen(true)
    } finally {
      setConfirmOpen(false)
    }
  }

  const handleDeleteAccount = () => setIsDeleteDialogOpen(true)
  const confirmDeleteAccount = async () => {
    try {
      setIsDeleting(true)
      setIsDeleteDialogOpen(false)
      await deleteAccount()
      navigate("/auth/signin")
    } catch (err) {
      console.error("Delete account error:", err)
      setAlertMsg(
        t("settings.deleteAccountError", {
          defaultValue: "Failed to delete account",
        })
      )
      setIsDeleting(false)
    } finally {
      setIsDeleting(false)
    }
  }

  const openMail = () => {
    const email = "support@aigree.app"
    const subject = encodeURIComponent("AIgree Support")
    window.location.href = `mailto:${email}?subject=${subject}`
  }

  return (
    <PageWrapper
      title={t("settings.title")}
      headerLeft={
        <button
          className="absolute left-0 inset-y-0 my-auto w-12 h-12 flex items-center justify-center z-10"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <img src={arrowLeft} alt="back" className="w-6 h-6 invert" />
        </button>
      }
      background="bg-dark-default"
    >
      {/* Profile Card */}
      <button
        className="w-full bg-light-default border border-light-default rounded-[16px] p-5 flex items-center shadow-sm mb-4"
        onClick={() => navigate("/edit-profile")}
      >
        {user?.photoURL ? (
          <img
            src={user.photoURL}
            alt="avatar"
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-[72px] h-[72px] rounded-full mr-4 object-cover"
          />
        ) : (
          <div className="w-[72px] h-[72px] rounded-full bg-dark-default text-light-default flex items-center justify-center mr-4">
            <span className="text-[28px] font-bold">{userInitials}</span>
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[22px] font-semibold text-dark-default truncate text-left">
            {user?.displayName || "User"}
          </div>
          <div className="text-[16px] text-dark-default truncate text-left">
            {user?.email || "user@example.com"}
          </div>
        </div>
        <div className="text-[26px] text-dark-default">›</div>
      </button>

      {/* Actions List */}
      <div className="bg-light-default border border-light-default rounded-[16px] overflow-hidden mb-4">
        <button
          className="w-full flex items-center justify-between h-16 px-5 bg-light-default"
          onClick={() => navigate("/my-documents")}
        >
          <div className="flex items-center">
            <img src={documentsIcon} alt="docs" className="w-6 h-6 mr-4" />
            <span className="text-[16px] font-semibold text-dark-default">
              {t("settings.myDocuments")}
            </span>
          </div>
          <span className="text-[26px] text-dark-default">›</span>
        </button>
        <div className="h-px bg-black-t-lighten-5 ml-16" />

        <button
          className="w-full flex items-center justify-between h-16 px-5 bg-light-default"
          onClick={() => navigate("/change-language")}
        >
          <div className="flex items-center">
            <img src={translateIcon} alt="translate" className="w-6 h-6 mr-4" />
            <span className="text-[16px] font-semibold text-dark-default">
              {t("settings.changeLanguage")}
            </span>
          </div>
          <span className="text-[26px] text-dark-default">›</span>
        </button>
        <div className="h-px bg-black-t-lighten-5 ml-16" />

        <button
          className="w-full flex items-center justify-between h-16 px-5 bg-light-default"
          onClick={openMail}
        >
          <div className="flex items-center">
            <img src={mailIcon} alt="mail" className="w-6 h-6 mr-4" />
            <span className="text-[16px] font-semibold text-dark-default">
              {t("settings.contactUs")}
            </span>
          </div>
          <span className="text-[26px] text-dark-default">›</span>
        </button>
        <div className="h-px bg-black-t-lighten-5 ml-16" />

        <button
          className="w-full flex items-center justify-between h-16 px-5 bg-light-default"
          onClick={() => navigate("/terms")}
        >
          <div className="flex items-center">
            <img src={filesFile06} alt="terms" className="w-6 h-6 mr-4" />
            <span className="text-[16px] font-semibold text-dark-default">
              {t("settings.termsAndPrivacy")}
            </span>
          </div>
          <span className="text-[26px] text-dark-default">›</span>
        </button>
      </div>

      {/* Logout */}
      <button
        className={`w-full bg-light-default border border-light-default rounded-[16px] py-[18px] px-5 text-center text-[16px] font-bold text-danger-default mb-4
    ${isDeleting ? "opacity-50" : ""}`}
        onClick={handleDeleteAccount}
        disabled={isDeleting}
      >
        {isDeleting ? t("settings.deleting") : t("settings.deleteAccount")}
      </button>

      <button
        className="w-full bg-light-default border border-light-default rounded-[16px] py-[18px] px-5 text-center text-[16px] font-bold text-danger-default"
        onClick={handleLogout}
      >
        {t("settings.logout")}
      </button>

      <AlertModal
        open={alertOpen}
        title={t("common.error", { defaultValue: "Error" })}
        message={alertMsg}
        onClose={() => setAlertOpen(false)}
      />
      <AlertModal
        open={confirmOpen}
        title={t("auth.logout", { defaultValue: "Log out" })}
        message={t("auth.logoutConfirm", {
          defaultValue: "Are you sure you want to logout?",
        })}
        onClose={() => setConfirmOpen(false)}
        actions={[
          {
            label: t("common.cancel", { defaultValue: "Cancel" }),
            variant: "default",
          },
          {
            label: t("auth.logout", { defaultValue: "Log out" }),
            variant: "destructive",
            onClick: confirmLogout,
          },
        ]}
      />
      <AlertModal
        open={isDeleteDialogOpen}
        title={t("settings.deleteAccount", { defaultValue: "Delete account" })}
        message={t("settings.deleteAccountConfirm", {
          defaultValue:
            "Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data including documents and analysis results.",
        })}
        onClose={() => setIsDeleteDialogOpen(false)}
        actions={[
          {
            label: t("common.cancel", { defaultValue: "Cancel" }),
            variant: "default",
          },
          {
            label: t("settings.deleteAccount", {
              defaultValue: "Delete account",
            }),
            variant: "destructive",
            onClick: confirmDeleteAccount,
          },
        ]}
      />
    </PageWrapper>
  )
}

export default Settings
